import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid } from '@mui/material';
import { Formik, Field, FormikErrors } from 'formik';
import {
	CerebroDrawer,
	CerebroHeading,
	CerebroForm,
	CerebroText,
	CerebroLabel,
	CerebroFileDragAndDrop,
	CerebroPrimaryButton,
} from '../../shared/components';
import { FormikUtils } from '../../shared/utils';
import React from 'react';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks/Queries';
import { getApiResponseData } from '../../shared/utils/axios';

interface IUploadModifiedContractDrawerProps {
	dealGuid: string | undefined;
	subscriptionGuid: string | undefined;
	companyGuid: string | undefined;
	isCompanySubscription: boolean;
	open: boolean;
	onClose: () => void;
}

export const UploadModifiedContractDrawer: React.FC<IUploadModifiedContractDrawerProps> = ({
	open,
	onClose,
	dealGuid,
	subscriptionGuid,
	companyGuid,
	isCompanySubscription,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);

	var validateForm = (values: UploadModifiedContractValues): UploadModifiedContractErrors => {
		const errors: UploadModifiedContractErrors = {};

		if (!values.uploads || values.uploads.length == 0) {
			errors.uploads = 'Must select the subscription agreement file.';
		}

		return errors;
	};

	const onSubmit = useMutation(
		async ({ values }: UploadModifiedContractMutationVariables) => {
			let formData = new FormData();
			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});
			}

			let endpoint = isCompanySubscription
				? CerebroApiEndpoints.Company.SubscriptionContractUpload(companyGuid, subscriptionGuid)
				: CerebroApiEndpoints.Deals.SubscriptionContractUpload(dealGuid, subscriptionGuid);

			const { data, error } = await getApiResponseData<void>(
				api.post(endpoint, formData, {
					headers: { 'Content-Type': 'multipart/form-data' },
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				snackbar.enqueueSnackbar('Subscription contract uploaded.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading>Upload Subscription Contract</CerebroHeading>

			<Formik
				initialValues={{
					uploads: [] as File[],
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(onSubmit.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={12}>
								<CerebroText sx={{ marginBottom: 3 }}>
									<FontAwesomeIcon icon={faInfoCircle} size="sm" /> &nbsp; Once the contract has been
									uploaded any previous version will be overridden.
								</CerebroText>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel required id="uploads">
									Modified Contract
								</CerebroLabel>
								<Field
									as={CerebroFileDragAndDrop}
									name="uploads"
									onlyPdf={true}
									multiple={false}
									sx={{ marginY: 3, width: '100%' }}
									error={FormikUtils.IsInvalid('uploads', errors, touched)}
								/>
								{FormikUtils.IsInvalid('uploads', errors, touched) && (
									<CerebroText variant="error">{errors.uploads}</CerebroText>
								)}
							</Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type UploadModifiedContractErrors = FormikErrors<UploadModifiedContractValues>;
type UploadModifiedContractMutationVariables = FormikUtils.MutateOnSubmitVariables<UploadModifiedContractValues>;
interface UploadModifiedContractValues {
	uploads: File[];
}
